/* eslint-disable no-console */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import DEBUG_FLAGS from 'config/DebugFlags';
import {
  BusinessChoiceType,
  FlowChoiceType,
  SupportedCountries,
} from 'features/utils/types';
import { AddressDetailState } from 'schemas/addressDetailsStateSchema';
import { AnswersState } from 'schemas/answerStateSchema';
import { ExtraService } from 'schemas/extraServiceSchema';
import { MapDetailState } from 'schemas/mapDetailSchema';

export type AnswerKeys = keyof AnswersState;

function getDefaultAddress(): AddressDetailState {
  if (window.location.href.includes(SupportedCountries.LUXEMBOURG)) {
    return {
      country: 'Luxembourg',
      countryCode: 'LU',
      postCode: '1200',
      geoCode: {
        lat: 49.76588202662784,
        lng: 6.08718641974375,
      },
    };
  }
  if (window.location.href.includes(SupportedCountries.FRANCE)) {
    return {
      country: 'France',
      countryCode: 'FR',
      postCode: '59000',
      geoCode: {
        lat: 50.631852054055436,
        lng: 3.0570601094452736,
      },
    };
  }
  return {
    country: 'Belgium',
    countryCode: 'BE',
    postCode: '4000',
    geoCode: {
      lat: 50.64894143209567,
      lng: 5.56544847917174,
    },
  };
}

const initialState: AnswersState = {
  businessChoice: undefined,
  flowChoice: undefined,
  address: undefined,
  // TODO: I'm taking these random values as initial address for the simple flow
  addressDetails: getDefaultAddress(),
  panelId: undefined,
  roofAngle: undefined,
  roofType: undefined,
  consumption: undefined,
  billAmount: undefined,
  inverter: undefined,
  counter: undefined,
  needBattery: false,
  needVehicle: false,
  mapDetail: undefined,
  numPanelsExplicit: undefined,
  extraServices: [],
};

const getSliceFromLocalStorage = () => {
  if (!DEBUG_FLAGS.NO_LOCALSTORAGE) {
    try {
      const persistedState = localStorage?.getItem('reduxState');
      if (persistedState) {
        const storedAnswers = JSON.parse(persistedState).answers;
        // simulate error
        if (DEBUG_FLAGS.SIMULATE_STORAGE_ERROR) {
          console.warn(`!! simulate local storage error !!`);
          delete storedAnswers.addressDetails;
        }
        // check object is valid
        // if (!compareObjectStructure(initialState, storedAnswers)) {
        //   console.warn(
        //     'Stored answers has wrong structure.. we reset storage data'
        //   );
        //   console.warn(`Requested structure:${Object.keys(initialState)}`);
        //   console.warn(`Storage structure:${Object.keys(storedAnswers)}`);
        //   return storedAnswers;
        // }
        return storedAnswers;
      }
    } catch (e) {
      console.warn(e);
    }
  }
  return initialState;
};

export const answersSlice = createSlice({
  name: 'answers',
  initialState: getSliceFromLocalStorage(),
  reducers: {
    resetAll: (
      state: AnswersState,
      action: PayloadAction<AnswersState | undefined>
    ) => {
      // !! this is important !! reset all first (to be sure not holding previous variables)
      Object.assign(state, initialState);
      // then possible override from payload
      if (action.payload) Object.assign(state, action.payload);
    },
    updateAdress: (state: AnswersState, action: PayloadAction<string>) => {
      state.address = action.payload;
    },
    updateAdressDetails: (
      state: AnswersState,
      action: PayloadAction<AddressDetailState>
    ) => {
      state.addressDetails = action.payload;
    },

    updatePanel: (state: AnswersState, action: PayloadAction<string>) => {
      state.panelId = action.payload;
    },

    updateRoofAngle: (state: AnswersState, action: PayloadAction<number>) => {
      state.roofAngle = action.payload;
    },
    updateRoofType: (state: AnswersState, action: PayloadAction<string>) => {
      state.roofType = action.payload;
    },

    updateMapDetails: (
      state: AnswersState,
      action: PayloadAction<MapDetailState | undefined>
    ) => {
      state.mapDetail = action.payload;
      if (action.payload !== undefined) state.numPanelsExplicit = undefined; // once updating the map, we remove possible "explicit" panels
    },

    updateConsumption: (
      state: AnswersState,
      action: PayloadAction<number | undefined>
    ) => {
      state.consumption = action.payload;
      state.billAmount = undefined;
    },

    updateInverter: (state: AnswersState, action: PayloadAction<string>) => {
      state.inverter = action.payload;
    },

    updateCounter: (
      state: AnswersState,
      action: PayloadAction<string | undefined>
    ) => {
      state.counter = action.payload;
    },

    updateBill: (
      state: AnswersState,
      action: PayloadAction<number | undefined>
    ) => {
      state.billAmount = action.payload;
      state.consumption = undefined;
    },

    updateBattery: (state: AnswersState, action: PayloadAction<boolean>) => {
      state.needBattery = action.payload;
    },

    updateVehicle: (state: AnswersState, action: PayloadAction<boolean>) => {
      state.needVehicle = action.payload;
    },

    updateNumPanelExplicit: (
      state: AnswersState,
      action: PayloadAction<number>
    ) => {
      state.numPanelsExplicit = action.payload;
    },

    updateFlowChoice: (
      state: AnswersState,
      action: PayloadAction<FlowChoiceType>
    ) => {
      state.flowChoice = action.payload;
    },
    updateBusinessChoice: (
      state: AnswersState,
      action: PayloadAction<BusinessChoiceType>
    ) => {
      state.businessChoice = action.payload;
    },
    updateExtraServices: (
      state: AnswersState,
      action: PayloadAction<ExtraService[]>
    ) => {
      state.extraServices = action.payload;
    },
  },
});

// export Action creators are generated for each case reducer function
export const {
  resetAll,
  updateAdress,
  updateAdressDetails,
  updatePanel,
  updateRoofAngle,
  updateRoofType,
  updateMapDetails,
  updateConsumption,
  updateCounter,
  updateInverter,
  updateBattery,
  updateBill,
  updateVehicle,
  updateNumPanelExplicit,
  updateFlowChoice,
  updateBusinessChoice,
  updateExtraServices,
} = answersSlice.actions;

// export Reducer for store
const AnswersReducer = answersSlice.reducer;
export default AnswersReducer;
